import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Route,
  Outlet,
} from "react-router-dom";
import { QuizProvider } from "@/contexts/QuizContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  ProtectedRoute,
  PublicRoute,
  RoleGuard,
  PermissionGuard,
} from "@/components/guards";
import { Roles } from "@/shared/constants/roles";
import { Permissions } from "@/shared/constants/permissions";
import { FullPageLoadingSpinner } from "@/components/ui/loading-spinner";

// Eager-loaded pages (critical for initial render)
import Login from "./pages/Login";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import QuizAttempts from "./pages/QuizAttempts";

// Lazy-loaded pages (split into separate chunks)
const Courses = lazy(() => import("./pages/Courses"));
const MyLearning = lazy(() => import("./pages/MyLearning"));
const Achievements = lazy(() => import("./pages/Achievements"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CourseSection = lazy(() => import("./pages/CourseSection"));
const LearningPaths = lazy(() => import("./pages/LearningPaths"));
const LearningPathDetail = lazy(() => import("./pages/LearningPathDetail"));
const Quiz = lazy(() => import("./pages/Quiz"));
const QuizAttemptResult = lazy(() => import("./pages/QuizAttemptResult"));
const Certificate = lazy(() => import('./pages/Certificate'));


// Admin pages (lazy-loaded - only for admin users)
const AdminCourses = lazy(() => import("./pages/admin/AdminCourses"));
const AdminCourseModules = lazy(() => import("./pages/admin/AdminCourseModules"));
const AdminModulesContents = lazy(() => import("./pages/admin/AdminModulesContents"));
const AdminPaths = lazy(() => import("./pages/admin/AdminLearningPaths"));
const AdminBadges = lazy(() => import("./pages/admin/AdminBadges"));
const AdminEmployees = lazy(() => import("./pages/admin/AdminEmployees"));
const AdminOrganization = lazy(() => import("./pages/admin/AdminOrganization"));
const Reports = lazy(() => import("./pages/admin/Reports"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAllCertificatesUser = lazy(() => import("./pages/admin/AdminAllCertificatesUser"));
const AdminCertificateUser = lazy(() => import("./pages/admin/AdminCertificateUser"));
const AdminSupervisorSubordinates = lazy(() => import("./pages/admin/AdminSupervisorSubordinates"));


const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <Suspense fallback={<FullPageLoadingSpinner />}>
          <Outlet />
        </Suspense>
      }
    >
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Public authenticated routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <Courses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-learning"
        element={
          <ProtectedRoute>
            <MyLearning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/achievements"
        element={
          <ProtectedRoute>
            <Achievements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:id"
        element={
          <ProtectedRoute>
            <CourseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RoleGuard  roles={[Roles.Admin, Roles.Supervisor]}>
            <AdminUsers />
          </RoleGuard>
        }
      />
      <Route
        path="/admin/supervisors/:supervisorId/subordinates"
        element={
          <ProtectedRoute>
            <AdminSupervisorSubordinates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/module/:moduleId/section/:contentId"
        element={
          <ProtectedRoute>
            <CourseSection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/module/:moduleId/quiz/:quizId"
        element={
          <ProtectedRoute>
            <Quiz />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/module/:moduleId/quiz/:quizId/attempts"
        element={
          <ProtectedRoute>
            <QuizAttempts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/module/:moduleId/quiz/:quizId/attempt/:attemptId"
        element={
          <ProtectedRoute>
            <QuizAttemptResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-paths"
        element={
          <ProtectedRoute>
            <LearningPaths />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-certificate"
        element={
          <ProtectedRoute>
            <Certificate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/certificate/users"
        element={
          <RoleGuard  roles={[Roles.Supervisor]}>
            <AdminCertificateUser />
          </RoleGuard>
        }
      />
      <Route
      path="/admin/certificate/all-users"
      element={
        <RoleGuard  roles={[Roles.Admin]}>
          <AdminAllCertificatesUser />
        </RoleGuard>
      }
      />
      <Route
        path="/learning-path/:id"
        element={
          <ProtectedRoute>
            <LearningPathDetail />
          </ProtectedRoute>
        }
      />

      {/* Admin routes with permission/role guards */}
      <Route
        path="/admin/courses"
        element={
          <PermissionGuard permissions={[Permissions.ManageCourses]}>
            <AdminCourses />
          </PermissionGuard>
        }
      />
      <Route
        path="/admin/courses/:courseId/modules"
        element={
          <PermissionGuard
            permissions={[Permissions.CreateModules, Permissions.EditModules]}
          >
            <AdminCourseModules />
          </PermissionGuard>
        }
      />

      <Route
        path="/admin/courses/:courseId/modules/:moduleId"
        element={
          <PermissionGuard permissions={[Permissions.ManageContent]}>
            <AdminModulesContents />
          </PermissionGuard>
        }
      />

      <Route
        path="/admin/paths"
        element={
          <PermissionGuard permissions={[Permissions.ManageLearningPaths]}>
            <AdminPaths />
          </PermissionGuard>
        }
      />
      <Route
        path="/admin/badges"
        element={
          <RoleGuard roles={[Roles.Admin, Roles.ContentManager]}>
            <AdminBadges />
          </RoleGuard>
        }
      />
      <Route
        path="/admin/organization"
        element={
          <RoleGuard roles={[Roles.Admin, Roles.Supervisor]}>
            <AdminOrganization />
          </RoleGuard>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <RoleGuard roles={[Roles.Admin]}>
            <Reports />
          </RoleGuard>
        }
      />

      {/* Catch-all route */}
      <Route path="*" element={<NotFound />} />
    </Route>
  )
);

const App = () => (
  <ErrorBoundary>
      <QuizProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <RouterProvider router={router} />
        </TooltipProvider>
      </QuizProvider>
  </ErrorBoundary>
);

export default App;
